import { inject, provide } from 'vue';
import type { InjectionKey } from 'vue';

/**
 * Who is looking at this page, as a view is allowed to know it.
 *
 * A module view cannot import from `apps/ui` — that is what keeps a module
 * buildable on its own — so it has no route to the session store, and until
 * this existed a module page could not hide a control it knew would be
 * refused. The SMTP credentials page was the first that had to.
 *
 * The core provides this once, in the shell; a view injects it. The kit is
 * where it lives because the kit is already what both sides build from, and
 * because `./ui` in the module contract is deliberately Vue-free — an
 * injection key is exactly the Vue dependency it exists to keep out.
 *
 * **Everything here is advisory.** The browser already holds every role the
 * user has, so nothing in this interface is a security boundary: the API
 * decides authorization on every request, and a hidden button stops nobody.
 * What it buys is a page that does not advertise what it will not do.
 */
export interface Viewer<TRole extends string = string> {
	/**
	 * Exact membership, with no implication anywhere.
	 *
	 * `core:admin` is not a superuser — it manages people, not resources — so
	 * an administrator who has not been granted `ses:admin` does not get the
	 * credential controls, and would be refused by the API if the page offered
	 * them.
	 *
	 * Scoped to the roles the view on screen may name: a module's own, plus
	 * `core:admin`, which is exactly the set its manifest may require. Asking
	 * about anything else answers `false`. That is not secrecy — it is so a
	 * module cannot come to depend on another module's roles, which it has no
	 * business branching on and which may not exist in the next deployment.
	 */
	can(role: TRole): boolean;

	/**
	 * Where to send someone who is signed out, carrying this page back.
	 *
	 * **Not for a lapsed write window.** `016` removed `isFresh` from here
	 * along with every pre-emptive warning: a view that predicted the verdict
	 * could be wrong about it and then warned about nothing at all (`D95`).
	 * A write is attempted, and the shell asks for a code where it stands.
	 */
	signInHref(): string;
}

/**
 * Exported for a host that cannot call `provideViewer` from inside a `setup`
 * — `app.provide(...)`, or a test mounting one view on its own. A module view
 * has no reason to touch it: `useViewer` is the way in, and the error it
 * throws is the point.
 */
export const VIEWER_KEY = Symbol('dashboard.viewer') as InjectionKey<Viewer>;

/** Called once by the core's shell. A module never calls this. */
export function provideViewer(viewer: Viewer): void {
	provide(VIEWER_KEY, viewer);
}

export class NoViewerError extends Error {
	readonly code = 'no_viewer';
}

/**
 * Throws when nothing provided a viewer, rather than answering "no" to
 * everything.
 *
 * A deny-all fallback would render a page that looks finished with its
 * controls quietly missing, and nobody would notice until a client did. This
 * fails the first time the page is opened, which is while someone is looking.
 */
export function useViewer<TRole extends string = string>(): Viewer<TRole> {
	const viewer = inject(VIEWER_KEY, undefined);
	if (viewer === undefined) {
		throw new NoViewerError(
			'no viewer: a view using useViewer() must render inside the dashboard shell, which calls provideViewer()',
		);
	}
	// No assertion needed: method parameters are bivariant, so a
	// `Viewer<string>` satisfies `Viewer<TRole>`. The narrowing that matters
	// is on the way out — `can` then only accepts this module's roles.
	return viewer;
}
